// API service for digital human and video generation
// Using relative paths to leverage the proxy configuration in package.json
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const PAAS_BASE_URL = process.env.REACT_APP_PAAS_BASE_URL || 'https://paas-testing.psyai.com';
const PAAS_APP_KEY = process.env.REACT_APP_PAAS_APP_KEY || 'testtesttesttesttesttesttest';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    if (!endpoint) {
      throw new Error('API endpoint is required');
    }

    const url = `${this.baseURL}${endpoint}`;
    console.log('Making API request to:', url);

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Include cookies for authentication
      ...options,
    };

    // Add auth token if available (fallback for token-based auth)
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('API Response:', result);
      return result;
    } catch (error) {
      console.error('API request failed:', {
        url,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // Digital Human APIs
  getMyAvatars = async (page = 1, limit = 12, search = '') => {
    const params = new URLSearchParams({ page, pageSize: limit });
    if (search) params.append('search', search);
    return this.request(`/avatar-list?${params}`);
  }

  getPublicAvatars = async (page = 1, limit = 12, search = '') => {
    const params = new URLSearchParams({ page, pageSize: limit });
    if (search) params.append('search', search);
    return this.request(`/avatar-list?${params}`);
  }

  getDashboardData = async () => {
    return this.request('/dashboard/data');
  }

  uploadVideo = async (formData) => {
    return this.request('/upload-video', {
      method: 'POST',
      body: formData,
      headers: {}, // Remove Content-Type to let browser set it for FormData
    });
  }

  createVideoModel = async (data) => {
    return this.request('/video/model', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  createAvatar = async (formData) => {
    return this.request('/avatars', {
      method: 'POST',
      body: formData,
      headers: {}, // Remove Content-Type to let browser set it for FormData
    });
  }

  deleteAvatar = async (virtualmanKey) => {
    if (!virtualmanKey) {
      throw new Error('Avatar key is required');
    }
    return this.request('/delete-avatar', {
      method: 'POST',
      body: JSON.stringify({ virtualmanKey }),
    });
  }

  // Voice Cloning APIs
  getMyVoices = async (page = 1, limit = 12, search = '') => {
    const params = new URLSearchParams({ page, pageSize: limit });
    if (search) params.append('search', search);
    return this.request(`/audio-clone-list?${params}`);
  }

  getPublicVoices = async (page = 1, limit = 12, search = '') => {
    // For now, return the same list as my voices since backend doesn't distinguish
    const params = new URLSearchParams({ page, pageSize: limit });
    if (search) params.append('search', search);
    return this.request(`/audio-clone-list?${params}`);
  }

  uploadAudio = async (formData) => {
    return this.request('/upload-audio', {
      method: 'POST',
      body: formData,
      headers: {}, // Remove Content-Type to let browser set it for FormData
    });
  }

  createAudioClone = async (data) => {
    return this.request('/create-audio-clone', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  cloneVoice = async (formData) => {
    // This is a two-step process: upload audio, then create clone
    try {
      // Step 1: Upload the audio file
      const uploadResult = await this.uploadAudio(formData);
      
      // Step 2: Create the audio clone using the uploaded file key
      const name = formData.get('name');
      const description = formData.get('description');
      const gender = formData.get('gender') || 'unknown';
      
      const cloneData = {
        name: name,
        description: description,
        gender: gender,
        fileKey: uploadResult.fileKey
      };
      
      return await this.createAudioClone(cloneData);
    } catch (error) {
      console.error('Voice cloning failed:', error);
      throw error;
    }
  }

  deleteVoice = async (timbreKey) => {
    if (!timbreKey) {
      throw new Error('Timbre key is required');
    }
    // Use the new DELETE endpoint
    return this.request(`/assets/audio-clone/${timbreKey}`, {
      method: 'DELETE',
    });
  }

  // Video Generation APIs
  generateVideo = async (data) => {
    return this.request('/video/base_vid/coze', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  runWorkflow = async (data) => {
    return this.request('/workflow/run', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getWorkflowResults = async (page = 1, limit = 10) => {
    const params = new URLSearchParams({ page, pageSize: limit });
    return this.request(`/workflow/results?${params.toString()}`);
  }

  getMyVideos = async (page = 1, limit = 12, search = '', groupId = '') => {
    const params = new URLSearchParams({ page, pageSize: limit });
    if (search) params.append('search', search);
    if (groupId) params.append('groupId', groupId);
    return this.request(`/video/base_vid/list?${params}`);
  }

  getVideoStatus = async (videoId) => {
    return this.request(`/check-video-task/${videoId}`);
  }

  // Video Management APIs
  deleteVideo = async (videoId) => {
    return this.request(`/video/base_vid/${videoId}`, {
      method: 'DELETE',
    });
  }

  batchDeleteVideos = async (videoIds) => {
    return this.request('/video/base_vid/batch', {
      method: 'DELETE',
      body: JSON.stringify({ ids: videoIds }),
    });
  }

  renameVideo = async (videoId, title) => {
    return this.request(`/video/base_vid/${videoId}/title`, {
      method: 'PUT',
      body: JSON.stringify({ title }),
    });
  }

  searchVideos = async (name, page = 1, pageSize = 10) => {
    const params = new URLSearchParams({ name, page, pageSize });
    return this.request(`/video/base_vid/search?${params}`);
  }

  // Video Group APIs
  getVideoGroups = async (page = 1, pageSize = 10) => {
    const params = new URLSearchParams({ page, pageSize });
    return this.request(`/video/base_vid/video/groups?${params}`);
  }

  createVideoGroup = async (name) =>
    this.request('/video/base_vid/video/groups', { method: 'POST', body: JSON.stringify({ name }) });

  renameVideoGroup = async (groupId, name) =>
    this.request(`/video/base_vid/video/groups/${groupId}`, { method: 'PUT', body: JSON.stringify({ name }) });

  deleteVideoGroup = async (groupId) =>
    this.request(`/video/base_vid/video/groups/${groupId}`, { method: 'DELETE' });

  assignVideosToGroup = async (groupId, videoIds) =>
    this.request(`/video/base_vid/video/groups/${groupId}/assign`, { method: 'POST', body: JSON.stringify({ videoIds }) });

  // Upload APIs
  uploadFile = async (file, type = 'video') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    return this.request('/upload', {
      method: 'POST',
      body: formData,
      headers: {}, // Remove Content-Type to let browser set it for FormData
    });
  }

  getUploadProgress = async (uploadId) => {
    return this.request(`/upload/${uploadId}/progress`);
  }

  // SORA Video Generation APIs
  uploadImageForSora = async (formData) => {
    // 使用现有的 /upload-audio 接口上传图片
    return this.request('/upload-audio', {
      method: 'POST',
      body: formData,
      headers: {}, // Remove Content-Type to let browser set it for FormData
    });
  }

  generateSoraVideo = async (data) => {
    // 调用 SORA 视频生成接口
    return this.request('/video/base_vid/sora', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Image Generation APIs
  generateImage = async (data) => {
    const url = `${API_BASE_URL}/video/base_vid/img`;

    const headers = {
      'Content-Type': 'application/json',
    };

    // Add AuthAppKey for authentication
    if (PAAS_APP_KEY) {
      headers.AuthAppKey = PAAS_APP_KEY;
    }

    // Add Authorization header if token is available
    const token = localStorage.getItem('auth_token') || localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(data),
      });

      const rawText = await response.text();
      let result;

      try {
        result = rawText ? JSON.parse(rawText) : null;
      } catch (parseError) {
        console.error('Failed to parse generate image response:', parseError, rawText);
      }

      if (!response.ok || (result && result.code !== undefined && result.code !== 0)) {
        const message = result?.message || result?.msg || rawText || response.statusText || '生成图片失败';
        throw new Error(message);
      }

      return result || { success: true };
    } catch (error) {
      console.error('Generate image API request failed:', error);
      throw error;
    }
  }

  getMyImages = async (page = 1, limit = 12, search = '', groupId = '') => {
    const params = new URLSearchParams({ page, pageSize: limit });
    if (search) params.append('search', search);
    if (groupId) params.append('groupId', groupId);
    const url = `${API_BASE_URL}/video/base_vid/img/list?${params}`;

    const headers = {
      'Content-Type': 'application/json',
    };

    // Add AuthAppKey for authentication
    if (PAAS_APP_KEY) {
      headers.AuthAppKey = PAAS_APP_KEY;
    }

    // Add Authorization header if token is available
    const token = localStorage.getItem('auth_token') || localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    console.log('Fetching images from:', url);

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers,
      });

      const rawText = await response.text();
      let result;

      try {
        result = rawText ? JSON.parse(rawText) : null;
      } catch (parseError) {
        console.error('Failed to parse get images response:', parseError, rawText);
      }

      if (!response.ok || (result && result.code !== undefined && result.code !== 0)) {
        const message = result?.message || result?.msg || rawText || response.statusText || '获取图片列表失败';
        throw new Error(message);
      }

      console.log('Get images API response:', result);

      // Return the entire response so the component can access data.items
      return result || { code: 0, msg: 'Success', data: { items: [], total: 0, page: 1, pageSize: limit } };
    } catch (error) {
      console.error('Get images API request failed:', error);
      throw error;
    }
  }

  // Image Management APIs
  deleteImage = async (imageId) => {
    return this.request(`/video/base_vid/img/${imageId}`, {
      method: 'DELETE',
    });
  }

  batchDeleteImages = async (imageIds) => {
    return this.request('/video/base_vid/img/batch', {
      method: 'DELETE',
      body: JSON.stringify({ ids: imageIds }),
    });
  }

  renameImage = async (imageId, title) => {
    return this.request(`/video/base_vid/img/${imageId}/title`, {
      method: 'PUT',
      body: JSON.stringify({ title }),
    });
  }

  searchImages = async (name, page = 1, pageSize = 10) => {
    const params = new URLSearchParams({ name, page, pageSize });
    const url = `${API_BASE_URL}/video/base_vid/img/search?${params}`;

    const headers = {
      'Content-Type': 'application/json',
    };

    if (PAAS_APP_KEY) {
      headers.AuthAppKey = PAAS_APP_KEY;
    }

    const token = localStorage.getItem('auth_token') || localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers,
      });

      const rawText = await response.text();
      let result;

      try {
        result = rawText ? JSON.parse(rawText) : null;
      } catch (parseError) {
        console.error('Failed to parse search images response:', parseError, rawText);
      }

      if (!response.ok || (result && result.code !== undefined && result.code !== 0)) {
        const message = result?.message || result?.msg || rawText || response.statusText || '搜索图片失败';
        throw new Error(message);
      }

      return result || { code: 0, msg: 'success', data: { items: [], total: 0, page, pageSize } };
    } catch (error) {
      console.error('Search images API request failed:', error);
      throw error;
    }
  }

  // Image Group APIs
  getImageGroups = async (page = 1, pageSize = 10) => {
    const params = new URLSearchParams({ page, pageSize });
    return this.request(`/video/base_vid/image/groups?${params}`);
  }

  createImageGroup = async (name) =>
    this.request('/video/base_vid/image/groups', { method: 'POST', body: JSON.stringify({ name }) });

  renameImageGroup = async (groupId, name) =>
    this.request(`/video/base_vid/image/groups/${groupId}`, { method: 'PUT', body: JSON.stringify({ name }) });

  deleteImageGroup = async (groupId) =>
    this.request(`/video/base_vid/image/groups/${groupId}`, { method: 'DELETE' });

  assignImagesToGroup = async (groupId, imageIds) =>
    this.request(`/video/base_vid/image/groups/${groupId}/assign`, { method: 'POST', body: JSON.stringify({ imageIds }) });
}

// Create and export a singleton instance
const apiServiceInstance = new ApiService();

// Debug logging to ensure the service is instantiated correctly
console.log('API Service initialized:', {
  instance: apiServiceInstance,
  baseURL: apiServiceInstance.baseURL,
  methods: Object.getOwnPropertyNames(Object.getPrototypeOf(apiServiceInstance))
});

export default apiServiceInstance;